import { IPuzzleRemoteDataV1 } from "./dataLayer/v1/interfaces/data/IPuzzleRemoteDataV1";
import { IWordInPuzzleDataV1 } from "./dataLayer/v1/interfaces/data/IWordInPuzzleDataV1";
import { IPlayerStats } from "./dataLayer/v1/interfaces/data/IPlayerStats";
import { getSimpleDateInSeattle } from "./dataLayer/getSimpleDateInSeattle";
import type {
  InvitePreview,
  LeaderboardEntry,
  Roster,
  PlayerPlay,
  FoundWord,
  OperationsDashboardData,
  PremiumSource,
  PremiumTier,
} from "./dataLayer/backendApi";

/**
 * Test mode lets us reach the app's screens without signing in or contacting
 * the backend — useful for UI/CSS work and for exercising specific game states
 * (e.g. "today's puzzle just played" before rare-word bonuses are counted).
 *
 * Activation: load the app with `?test` in the URL (e.g.
 * http://localhost:8080/?test). The flag is persisted to localStorage so it
 * survives reloads; load with `?test=0` to turn it back off.
 *
 * In test mode the app uses a fake signed-in identity (nothing is written to
 * the real auth localStorage keys) and treats solution submission as a no-op.
 * `?test` lands on a scenario picker (see TestScenariosScreen) so you can jump
 * straight into a chosen state with canned data.
 */

const TEST_MODE_KEY = "WirderTestMode";

export function isAppleStorePreview(): boolean {
  return (
    isTestMode() && new URLSearchParams(window.location.search).get("storePreview") === "apple"
  );
}

export function isTestMode(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has("test")) {
      const enabled = params.get("test") !== "0" && params.get("test") !== "false";
      if (enabled) {
        localStorage.setItem(TEST_MODE_KEY, "1");
      } else {
        localStorage.removeItem(TEST_MODE_KEY);
      }
      return enabled;
    }
    return localStorage.getItem(TEST_MODE_KEY) === "1";
  } catch {
    return false;
  }
}

const TEST_RUBRIC = {
  // index = (word length - 1), value = points (0 = invalid/too-short length)
  wordLengthPoints: [0, 0, 1, 2, 4, 7, 11, 16, 22],
  goldCriteria: { maxPercent: 1, bonus: 100 },
  silverCriteria: { maxPercent: 5, bonus: 50 },
  bronzeCriteria: { maxPercent: 15, bonus: 25 },
};

const TEST_BOARD = [
  ["W", "I", "R", "D"],
  ["E", "R", "T", "E"],
  ["S", "T", "A", "B"],
  ["O", "L", "M", "G"],
];

/** SimpleDate `daysAgo` days before today (Seattle). */
function dateDaysAgo(daysAgo: number): { year: number; month: number; day: number } {
  const today = getSimpleDateInSeattle();
  const d = new Date(today.year, today.month - 1, today.day);
  d.setDate(d.getDate() - daysAgo);
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

function simpleDateId(date: { year: number; month: number; day: number }): string {
  return `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
}

/** Today's puzzle, in progress (not yet played) — matches an in-progress daily. */
function buildTodayInProgressData(): IPuzzleRemoteDataV1 {
  return {
    date: getSimpleDateInSeattle(),
    board: TEST_BOARD,
    scoringRubric: TEST_RUBRIC,
    solution: {
      totalSubmissions: undefined,
      wordsInPuzzle: [],
    },
    timeLimitInSeconds: 300,
    wordsFound: [],
    played: false,
    totalPoints: undefined,
    regularPoints: 0,
    rarityPoints: undefined,
    timeElapsedInSeconds: 0,
  };
}

function buildTodayExpiredData(): IPuzzleRemoteDataV1 {
  return {
    ...buildTodayInProgressData(),
    timeElapsedInSeconds: 300,
  };
}

/**
 * Today's puzzle after the player has submitted it. Rarity is still unknown
 * (totalSubmissions undefined), so only regular points are known — the final
 * score isn't revealed until the next-day rollover.
 */
function buildTodayPlayedData(): IPuzzleRemoteDataV1 {
  return {
    date: getSimpleDateInSeattle(),
    board: TEST_BOARD,
    scoringRubric: TEST_RUBRIC,
    solution: {
      totalSubmissions: undefined,
      wordsInPuzzle: [],
    },
    timeLimitInSeconds: 300,
    wordsFound: [
      "TIE",
      "RED",
      "REST",
      "WIRE",
      "TIRE",
      "RIDE",
      "DIRE",
      "RITE",
      "TIER",
      "WIRED",
      "TIRED",
      "TRIED",
      "WIRDER",
    ],
    played: true,
    totalPoints: undefined,
    regularPoints: 0,
    rarityPoints: undefined,
    timeElapsedInSeconds: 218,
  };
}

function buildPlayedTestPuzzleData(daysAgo: number, wordsFound: string[]): IPuzzleRemoteDataV1 {
  return {
    date: dateDaysAgo(daysAgo),
    board: TEST_BOARD,
    scoringRubric: TEST_RUBRIC,
    solution: {
      totalSubmissions: 100,
      wordsInPuzzle: [],
    },
    timeLimitInSeconds: 300,
    wordsFound: Array.from(new Set(["TIE", "RED", "REST", ...wordsFound])),
    played: true,
    totalPoints: undefined,
    regularPoints: 0,
    rarityPoints: undefined,
    timeElapsedInSeconds: 300,
  };
}

function buildSkippedTestPuzzleData(daysAgo: number): IPuzzleRemoteDataV1 {
  return {
    date: dateDaysAgo(daysAgo),
    board: TEST_BOARD,
    scoringRubric: TEST_RUBRIC,
    solution: {
      totalSubmissions: 100,
      wordsInPuzzle: [],
    },
    timeLimitInSeconds: 300,
    wordsFound: [],
    played: false,
    totalPoints: undefined,
    regularPoints: 0,
    rarityPoints: undefined,
    timeElapsedInSeconds: 0,
  };
}

/**
 * A finished past puzzle with a full community solution list (timesFound per
 * word) so the review screen shows real rare-word medals and a final score.
 * Rarity: timesFound 1 = Gold, 2 = Silver, 3 = Bronze; otherwise by percent of
 * the 100 total submissions (Gold <=1%, Silver <=5%, Bronze <=15%).
 */
function buildPastFinishedData(daysAgo: number): IPuzzleRemoteDataV1 {
  const wordsInPuzzle: IWordInPuzzleDataV1[] = [
    { word: "WIRDER", timesFound: 1 }, // Gold (1%)
    { word: "WIRED", timesFound: 2 }, // Silver (2%)
    { word: "TRIED", timesFound: 10 }, // Bronze (10%)
    { word: "TIRED", timesFound: 34 },
    { word: "DIRE", timesFound: 22 },
    { word: "RITE", timesFound: 26 },
    { word: "TIER", timesFound: 24 },
    { word: "WIRE", timesFound: 61 },
    { word: "TIRE", timesFound: 58 },
    { word: "RIDE", timesFound: 44 },
    { word: "REST", timesFound: 72 },
    { word: "RED", timesFound: 88 },
    { word: "TIE", timesFound: 80 },
    { word: "RAT", timesFound: 51 }, // not found by the player
    { word: "RATE", timesFound: 33 }, // not found by the player
  ];

  // Words the player found (includes the three medal words plus regulars).
  const wordsFound = [
    "WIRDER",
    "WIRED",
    "TRIED",
    "DIRE",
    "TIER",
    "WIRE",
    "TIRE",
    "RIDE",
    "REST",
    "RED",
    "TIE",
  ];

  // Gold 100 + Silver 50 + Bronze 25 (TRIED) = 175 from the medal words found.
  return {
    date: dateDaysAgo(daysAgo),
    board: TEST_BOARD,
    scoringRubric: TEST_RUBRIC,
    solution: {
      totalSubmissions: 100,
      wordsInPuzzle,
    },
    timeLimitInSeconds: 300,
    wordsFound,
    played: true,
    totalPoints: undefined,
    regularPoints: 0,
    rarityPoints: 175,
    timeElapsedInSeconds: 264,
  };
}

/**
 * A past puzzle whose community solution is known (so per-word "times found"
 * counts render) but whose rare-word bonus hasn't been scored for this player
 * yet. Reproduces the window between a puzzle's solution publishing and the
 * player's scoredPlay appearing (rollover in progress / transient scoredPlay
 * 404). The review must show the rare-word bonus + total as *pending*, not a
 * misleading "0", because the score is unknown rather than zero. See issue #39.
 */
function buildPastPendingData(daysAgo: number): IPuzzleRemoteDataV1 {
  return {
    ...buildPastFinishedData(daysAgo),
    // Community counts are known; the player's play is recorded (played=true),
    // but the scored play carrying rarity hasn't landed yet.
    totalPoints: undefined,
    rarityPoints: undefined,
  };
}

/**
 * A history of past puzzles (oldest first), so the Home screen renders several
 * day cards with varied word counts. Includes both a single skipped day (Jul 4
 * in a 6..1-days-ago window) and a multi-day gap (days 7 & 8 ago have no entry
 * at all) so we can confirm the "recent days" row collapses each gap — however
 * many days wide — into a single ellipsis marker, never two side by side.
 */
function buildHistoryPuzzles(): IPuzzleRemoteDataV1[] {
  return [
    buildPlayedTestPuzzleData(9, ["CORE", "ROSE", "SORE", "ORE", "COS"]),
    // days 8 and 7 have no entry — a two-day gap before day 6.
    buildPlayedTestPuzzleData(6, ["WIRE", "REST", "TIRE", "RIDE", "DIRE", "TIE"]),
    buildPlayedTestPuzzleData(5, ["RAT", "TAB", "BAT", "ART"]),
    buildSkippedTestPuzzleData(4),
    buildPlayedTestPuzzleData(3, ["MOLE", "MOST", "SLOT", "LOT", "TOM", "OLE", "STAB", "BLAST"]),
    buildPlayedTestPuzzleData(2, ["WIRDER", "WIRE", "RED"]),
    buildPlayedTestPuzzleData(1, ["TAR", "RAT", "ART", "TARE", "RATE", "TEAR", "EAT", "ATE"]),
  ];
}

/** Screens a test scenario can drop you into. */
export type TestTargetScreen =
  | "Home"
  | "Puzzle"
  | "ReviewPuzzle"
  | "Connecting"
  | "Friends"
  | "Operations"
  | "Leaderboard"
  | "PlayerStats"
  | "Settings";

export interface ITestScenarioSetup {
  puzzles: IPuzzleRemoteDataV1[];
  /** Index of the puzzle to make "current" (for Puzzle/ReviewPuzzle screens). */
  currentPuzzleIndex: number;
  screen: TestTargetScreen;
  /** Optional rollover-generated baseline used by Stats scenarios. */
  playerStats?: IPlayerStats;
  /** Optional fake account identity for account-switch scenarios. */
  playerId?: string;
  playerName?: string;
  /**
   * Optional override for the Home screen's today-puzzle status. Use "loading"
   * or "notReady" (with a puzzle list that omits today) to exercise the
   * "no puzzle ready yet" hero cards.
   */
  todayPuzzleStatus?: "idle" | "loading" | "notReady";
  /**
   * Optional pending friend-invite to seed, mirroring what a `/?invite={code}`
   * deep link produces. When set, the Friends screen shows the "… invited you"
   * accept/dismiss banner so the invite-follow flow can be tried offline.
   */
  pendingInvite?: { code: string; preview: InvitePreview | null };
  operationsDashboard?: OperationsDashboardData;
  /** The signed-in player's recognition tier; null means no premium access. */
  premiumTier?: PremiumTier | null;
  /** Optional unresolved premium state for deterministic Home indicator checks. */
  premiumStatus?: "idle" | "loading";
  /** Simulate a failed server-side premium verification for fail-closed UI tests. */
  premiumVerificationError?: boolean;
  /** Keep Patreon linked even when the initial effective tier is null. */
  premiumLinked?: boolean;
  /** Deterministic result applied by "Check membership again". */
  premiumRefreshOutcome?: PremiumTier | "error";
  /** Ordered results for repeated return/refresh checks; the last result repeats. */
  premiumRefreshSequence?: Array<PremiumTier | null | "error">;
  /** Optional delay for observing in-flight refresh loading/deduplication. */
  premiumRefreshDelayMs?: number;
  /** Short deterministic backoff delays for post-purchase retry scenarios. */
  premiumPurchaseRetryDelaysMs?: number[];
  /** Effective entitlement source; complimentary grants never trigger paid-member thanks. */
  premiumSource?: PremiumSource;
  /** Previous confirmed paid tier to seed before an automatic refresh. */
  premiumPreviousPaidTier?: PremiumTier | null;
  /** Recent membership-link click, or restore its account-scoped stored value. */
  premiumPurchaseIntent?: boolean | "restore";
  /** Directly show the one-time thank-you experience for visual/accessibility checks. */
  premiumCelebration?: { tier: PremiumTier; previousTier: PremiumTier | null };
  /** Optional Home notice, including the post-link no-tier recovery action. */
  premiumNotice?: { message: string; action?: "join" };
}

export interface ITestScenario {
  id: string;
  label: string;
  description: string;
  build: () => ITestScenarioSetup;
}

/**
 * The catalog of jump-in test scenarios shown by the scenario picker. Each one
 * assembles a canned session and picks the screen to open.
 */
export function getTestScenarios(): ITestScenario[] {
  return [
    {
      id: "operations-dashboard",
      label: "Operations dashboard",
      description: "Responsive operational metrics and interactive daily activity chart.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Operations",
          operationsDashboard: buildTestOperationsDashboard(),
        };
      },
    },
    {
      id: "connecting",
      label: "Connecting to server",
      description:
        "The launch reconnect screen for a signed-in player \u2014 logo flies to the corner, then Home.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return { puzzles, currentPuzzleIndex: puzzles.length - 1, screen: "Connecting" };
      },
    },
    {
      id: "today-playing",
      label: "Today \u2014 playing",
      description: "Jump straight into today\u2019s puzzle, in progress.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return { puzzles, currentPuzzleIndex: puzzles.length - 1, screen: "Puzzle" };
      },
    },
    {
      id: "today-expired-home",
      label: "Today \u2014 expired on Home",
      description:
        "An unsubmitted puzzle with no time left; continuing goes straight to submission.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayExpiredData()];
        return { puzzles, currentPuzzleIndex: puzzles.length - 1, screen: "Home" };
      },
    },
    {
      id: "today-played-home",
      label: "Today \u2014 just played (Home)",
      description: "Home screen showing today\u2019s results card, before rare-word bonuses.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayPlayedData()];
        return { puzzles, currentPuzzleIndex: puzzles.length - 1, screen: "Home" };
      },
    },
    {
      id: "today-played-review",
      label: "Today \u2014 just played (Review)",
      description: "Review today\u2019s puzzle: regular points only, rarity still unknown.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayPlayedData()];
        return { puzzles, currentPuzzleIndex: puzzles.length - 1, screen: "ReviewPuzzle" };
      },
    },
    {
      id: "home-history",
      label: "Home \u2014 with history",
      description: "Home screen with several past days and today still to play.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return { puzzles, currentPuzzleIndex: puzzles.length - 1, screen: "Home" };
      },
    },
    {
      id: "premium-home-bronze",
      label: "Home — Bronze backer",
      description: "Home leaderboard link with the Bronze supporter star.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: "bronze",
        };
      },
    },
    {
      id: "premium-home-silver",
      label: "Home — Silver backer",
      description: "Home leaderboard link with the Silver supporter star.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: "silver",
        };
      },
    },
    {
      id: "premium-home-gold",
      label: "Home — Gold backer",
      description: "Home leaderboard link with the Gold supporter star.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: "gold",
        };
      },
    },
    {
      id: "premium-home-nonbacker",
      label: "Home — not a backer",
      description: "Home leaderboard link with the neutral premium-feature star.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: null,
        };
      },
    },
    {
      id: "premium-home-verification-error",
      label: "Home — verification error",
      description: "Home stays neutral when backer access cannot be verified.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: null,
          premiumVerificationError: true,
        };
      },
    },
    {
      id: "premium-home-loading",
      label: "Home — checking backer access",
      description: "Home stays neutral while backer access is loading.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: null,
          premiumStatus: "loading",
        };
      },
    },
    {
      id: "premium-leaderboard-backer",
      label: "Leaderboard — backer",
      description: "Full rankings and scores with Bronze, Silver, and Gold supporter stars.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Leaderboard",
          premiumTier: "gold",
        };
      },
    },
    {
      id: "premium-leaderboard-nonbacker",
      label: "Leaderboard — not a backer",
      description:
        "Accepted friends remain visible, while ranks and scores are replaced by the Patreon invitation.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Leaderboard",
          premiumTier: null,
        };
      },
    },
    {
      id: "premium-leaderboard-refresh-granted",
      label: "Leaderboard — membership refresh grants access",
      description:
        "Starts names-only with Patreon linked; checking again unlocks scores and opens the Silver thank-you.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Leaderboard",
          premiumTier: null,
          premiumLinked: true,
          premiumRefreshOutcome: "silver",
          premiumPreviousPaidTier: null,
        };
      },
    },
    {
      id: "premium-leaderboard-refresh-failed",
      label: "Leaderboard — membership refresh fails",
      description:
        "Starts names-only with Patreon linked; a failed check remains fail-closed without friend stars.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Leaderboard",
          premiumTier: null,
          premiumLinked: true,
          premiumRefreshOutcome: "error",
          premiumRefreshDelayMs: 350,
        };
      },
    },
    {
      id: "premium-leaderboard-verification-error",
      label: "Leaderboard — verification error",
      description:
        "Accepted friends remain visible without supporter tiers or scores while verification is unavailable.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Leaderboard",
          premiumTier: null,
          premiumVerificationError: true,
        };
      },
    },
    {
      id: "stats-today-records",
      label: "Stats — today's records pending",
      description:
        "Today's submitted puzzle immediately replaces lower personal records while community ranks remain pending.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayPlayedData()];
        const playerStats = buildTestPlayerStats();
        playerStats.mostFoundWords = {
          value: 10,
          placeOverall: 8,
          date: dateDaysAgo(5),
        };
        playerStats.totalPoints = {
          value: 20,
          placeOverall: 10,
          date: dateDaysAgo(5),
        };
        playerStats.regularPoints = {
          value: 20,
          placeOverall: 10,
          date: dateDaysAgo(5),
        };
        playerStats.letters[4] = {
          value: 5,
          placeOverall: 2,
          date: dateDaysAgo(3),
        };
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "PlayerStats",
          premiumTier: "silver",
          playerStats,
        };
      },
    },
    {
      id: "premium-stats-backer",
      label: "Stats — Silver backer",
      description: "Silver backer stats with per-word-length records unlocked.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "PlayerStats",
          premiumTier: "silver",
        };
      },
    },
    {
      id: "premium-stats-nonbacker",
      label: "Stats — not a backer",
      description:
        "Core personal records remain visible, with word-length records replaced by a friendly Patreon invitation.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "PlayerStats",
          premiumTier: null,
        };
      },
    },
    {
      id: "premium-settings-backer",
      label: "Settings — Silver backer",
      description: "Settings with a linked Silver Patreon membership.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: "silver",
        };
      },
    },
    {
      id: "premium-settings-nonbacker",
      label: "Settings — not a backer",
      description: "Settings invitation with the neutral premium-feature star.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: null,
        };
      },
    },
    {
      id: "premium-settings-linked-nonmember",
      label: "Settings — linked without a membership",
      description:
        "Patreon is linked without a paid tier; joining remains primary while refresh and unlink stay secondary.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: null,
          premiumLinked: true,
        };
      },
    },
    {
      id: "premium-link-complete-no-tier",
      label: "Membership — link complete, no paid tier",
      description:
        "Home callback notice links directly to joining Patreon instead of ending in a dead end.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: null,
          premiumLinked: true,
          premiumNotice: {
            message: "Patreon is linked, but a Wirder membership wasn't found yet.",
            action: "join",
          },
        };
      },
    },
    {
      id: "premium-delayed-propagation",
      label: "Membership — delayed Patreon propagation",
      description:
        "The first return is pending, then a short automatic retry detects Bronze and celebrates.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: null,
          premiumLinked: true,
          premiumRefreshSequence: [null, "bronze"],
          premiumRefreshDelayMs: 150,
          premiumPurchaseRetryDelaysMs: [250, 500],
          premiumPreviousPaidTier: null,
          premiumPurchaseIntent: true,
        };
      },
    },
    {
      id: "premium-purchase-unlinked",
      label: "Membership — purchase needs Patreon link",
      description:
        "A purchaser without a Patreon link is guided into secure account linking instead of impossible entitlement polling.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: null,
          premiumLinked: false,
          premiumRefreshSequence: [null],
          premiumPurchaseRetryDelaysMs: [50, 50],
          premiumPreviousPaidTier: null,
          premiumPurchaseIntent: true,
        };
      },
    },
    {
      id: "premium-purchase-intent-restore",
      label: "Membership — restore purchase intent",
      description:
        "After a purchase-intent scenario, this same account restores the explicit subscription retry.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: null,
          premiumLinked: true,
          premiumPurchaseIntent: "restore",
        };
      },
    },
    {
      id: "premium-purchase-intent-other-account",
      label: "Membership — purchase intent on another account",
      description:
        "A different linked account has no new-subscription intent and keeps the generic membership check.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          playerId: "test-member-no-intent",
          playerName: "No-intent test account",
          premiumTier: null,
          premiumLinked: true,
        };
      },
    },
    {
      id: "premium-subscription-retry-feedback",
      label: "Membership — subscription retry feedback",
      description:
        "A forced retry first reports no propagated tier, then a second retry detects Bronze.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: null,
          premiumLinked: true,
          premiumRefreshSequence: [null, null, "bronze"],
          premiumRefreshDelayMs: 350,
          premiumPurchaseRetryDelaysMs: [5000, 5000],
          premiumPreviousPaidTier: null,
          premiumPurchaseIntent: true,
        };
      },
    },
    {
      id: "premium-purchase-retries-exhausted",
      label: "Membership — confirmation retries exhausted",
      description:
        "Successful no-tier responses exhaust the bounded retries while reassurance and manual actions remain.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: null,
          premiumLinked: true,
          premiumRefreshSequence: [null],
          premiumRefreshDelayMs: 100,
          premiumPurchaseRetryDelaysMs: [200, 300],
          premiumPreviousPaidTier: null,
          premiumPurchaseIntent: true,
        };
      },
    },
    {
      id: "premium-celebration-new",
      label: "Celebration — new member Bronze",
      description:
        "Tap Simulate return to detect a first paid Bronze tier and open its joining thank-you.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: null,
          premiumLinked: true,
          premiumRefreshOutcome: "bronze",
          premiumPreviousPaidTier: null,
        };
      },
    },
    {
      id: "premium-celebration-acknowledged",
      label: "Membership — Bronze unchanged",
      description:
        "Tap Simulate return; the unchanged Bronze tier refreshes without another modal.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: "bronze",
          premiumLinked: true,
          premiumRefreshOutcome: "bronze",
          premiumPreviousPaidTier: "bronze",
        };
      },
    },
    {
      id: "premium-celebration-upgrade",
      label: "Celebration — upgraded to Silver",
      description:
        "Tap Simulate return to upgrade confirmed Bronze access and open the Silver thank-you.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: "bronze",
          premiumLinked: true,
          premiumRefreshOutcome: "silver",
          premiumPreviousPaidTier: "bronze",
        };
      },
    },
    {
      id: "premium-celebration-gold-upgrade",
      label: "Celebration — upgraded to Gold",
      description:
        "Tap Simulate return to skip from confirmed Bronze access to the Gold thank-you.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: "bronze",
          premiumLinked: true,
          premiumRefreshOutcome: "gold",
          premiumPreviousPaidTier: "bronze",
        };
      },
    },
    {
      id: "premium-celebration-silver-preview",
      label: "Celebration — new member Silver",
      description: "The first-join thank-you with Silver named in the membership content.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: "silver",
          premiumCelebration: { tier: "silver", previousTier: null },
        };
      },
    },
    {
      id: "premium-celebration-preview",
      label: "Celebration — new member Gold",
      description: "The polished Gold thank-you modal without requiring a focus event.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          premiumTier: "gold",
          premiumCelebration: { tier: "gold", previousTier: null },
        };
      },
    },
    {
      id: "premium-complimentary-grant",
      label: "Membership — complimentary Gold",
      description: "Complimentary Gold access remains active without a paid-member celebration.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: "gold",
          premiumLinked: false,
          premiumSource: "complimentary",
        };
      },
    },
    {
      id: "premium-complimentary-patreon-pending",
      label: "Membership — complimentary Gold awaiting Patreon",
      description:
        "Complimentary Gold remains active through no-tier and error checks, then a paid Bronze membership is recognized independently.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: "gold",
          premiumLinked: true,
          premiumSource: "complimentary",
          premiumRefreshSequence: [null, "error", "bronze"],
          premiumRefreshDelayMs: 150,
          premiumPurchaseRetryDelaysMs: [1200, 1200, 5000],
          premiumPreviousPaidTier: null,
          premiumPurchaseIntent: true,
        };
      },
    },
    {
      id: "premium-return-verification-error",
      label: "Membership — pending request error",
      description:
        "The post-purchase check fails as a request error, distinct from a successful no-tier response.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: null,
          premiumLinked: true,
          premiumRefreshOutcome: "error",
          premiumPurchaseRetryDelaysMs: [5000],
          premiumPreviousPaidTier: null,
          premiumPurchaseIntent: true,
        };
      },
    },
    {
      id: "premium-existing-entitlement-error",
      label: "Membership — existing tier refresh error",
      description:
        "A transient refresh error preserves verified Bronze access and reports the refresh problem.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: "bronze",
          premiumLinked: true,
          premiumRefreshOutcome: "error",
          premiumPreviousPaidTier: "bronze",
        };
      },
    },
    {
      id: "premium-purchase-error-retries-exhausted",
      label: "Membership — request retries exhausted",
      description:
        "Repeated request failures exhaust automatic checks and direct the player to the manual retry.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Settings",
          premiumTier: null,
          premiumLinked: true,
          premiumRefreshOutcome: "error",
          premiumRefreshDelayMs: 100,
          premiumPurchaseRetryDelaysMs: [200, 300],
          premiumPreviousPaidTier: null,
          premiumPurchaseIntent: true,
        };
      },
    },
    {
      id: "premium-account-a-new",
      label: "Membership account switch 1/3 — Alex joins",
      description:
        "Tap Simulate return, dismiss Alex's Bronze thank-you, then open Scenarios for step 2.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          playerId: "test-member-alex",
          playerName: "Alex (test account)",
          premiumTier: null,
          premiumLinked: true,
          premiumRefreshOutcome: "bronze",
          premiumPreviousPaidTier: null,
        };
      },
    },
    {
      id: "premium-account-b-new",
      label: "Membership account switch 2/3 — Blair joins",
      description:
        "Tap Simulate return; Blair receives an independent Bronze thank-you, then continue to step 3.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          playerId: "test-member-blair",
          playerName: "Blair (test account)",
          premiumTier: null,
          premiumLinked: true,
          premiumRefreshOutcome: "bronze",
          premiumPreviousPaidTier: null,
        };
      },
    },
    {
      id: "premium-account-a-return",
      label: "Membership account switch 3/3 — Alex returns",
      description:
        "After step 1, tap Simulate return; Alex already has Bronze, so an unchanged refresh shows no celebration.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          playerId: "test-member-alex",
          playerName: "Alex (test account)",
          premiumTier: "bronze",
          premiumLinked: true,
          premiumRefreshOutcome: "bronze",
        };
      },
    },
    {
      id: "today-loading",
      label: "Today \u2014 loading (not published yet)",
      description:
        "Home while we poke the server and wait for today\u2019s puzzle to publish \u2014 shows the loading card.",
      build: () => {
        // No today puzzle in the list; the loading hero is driven by status.
        const puzzles = buildHistoryPuzzles();
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          todayPuzzleStatus: "loading",
        };
      },
    },
    {
      id: "today-not-ready",
      label: "Today \u2014 not ready yet",
      description:
        "Home after the poke + retry budget elapsed without today\u2019s puzzle \u2014 shows the \u201Ccheck back shortly\u201D card.",
      build: () => {
        const puzzles = buildHistoryPuzzles();
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Home",
          todayPuzzleStatus: "notReady",
        };
      },
    },
    {
      id: "review-past",
      label: "Past day \u2014 Review (final)",
      description: "Review a finished past puzzle with rare-word medals and a final score.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        // Replace the most recent past day with the richer finished puzzle.
        const finishedIndex = puzzles.length - 2;
        puzzles[finishedIndex] = buildPastFinishedData(1);
        return { puzzles, currentPuzzleIndex: finishedIndex, screen: "ReviewPuzzle" };
      },
    },
    {
      id: "invite-follow",
      label: "Invite \u2014 following a link",
      description:
        "What you see after opening a friend\u2019s /?invite= link: the Friends screen with an accept/dismiss banner.",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        return {
          puzzles,
          currentPuzzleIndex: puzzles.length - 1,
          screen: "Friends",
          premiumTier: null,
          pendingInvite: {
            code: "test-incoming-invite-0001",
            preview: {
              fromDisplayName: "Katherine",
              createdAt: isoDaysFromNow(-1),
              expiresAt: isoDaysFromNow(13),
              status: "pending",
            },
          },
        };
      },
    },
    {
      id: "review-past-pending",
      label: "Past day \u2014 Review (bonus pending)",
      description:
        "Review a past puzzle whose community counts are in but the rare-word bonus hasn\u2019t been scored yet \u2014 must show a pending state, not 0 (issue #39).",
      build: () => {
        const puzzles = [...buildHistoryPuzzles(), buildTodayInProgressData()];
        const pendingIndex = puzzles.length - 2;
        puzzles[pendingIndex] = buildPastPendingData(1);
        return { puzzles, currentPuzzleIndex: pendingIndex, screen: "ReviewPuzzle" };
      },
    },
  ];
}

export function buildTestOperationsDashboard(): OperationsDashboardData {
  const dailyActivity = Array.from({ length: 60 }, (_, index) => {
    const date = dateDaysAgo(59 - index);
    const plays = 18 + ((index * 13) % 47) + Math.floor(index / 6);
    const logins = 7 + ((index * 5) % 19);
    const unknownPlays = index % 5 === 0 ? 2 : 0;
    const unknownLogins = index % 7 === 0 ? 1 : 0;
    const knownPlays = plays - unknownPlays;
    const knownLogins = logins - unknownLogins;
    const iosPlays = Math.floor(knownPlays * 0.3);
    const iosLogins = Math.floor(knownLogins * 0.35);
    const androidPlays = Math.floor(knownPlays * 0.2);
    const androidLogins = Math.floor(knownLogins * 0.2);
    return {
      date: simpleDateId(date),
      plays,
      logins,
      activePlayers: 45 + ((index * 7) % 34) + Math.floor(index / 8),
      byClient: [
        {
          surface: "ios_app" as const,
          appVersion: "1.0",
          webVersion: "0.1.66",
          plays: iosPlays,
          logins: iosLogins,
        },
        {
          surface: "android_app" as const,
          appVersion: "1.0.1",
          webVersion: "0.1.66",
          plays: androidPlays,
          logins: androidLogins,
        },
        {
          surface: "web" as const,
          appVersion: "0.1.66",
          webVersion: "0.1.66",
          plays: knownPlays - iosPlays - androidPlays,
          logins: knownLogins - iosLogins - androidLogins,
        },
        {
          surface: "unknown" as const,
          appVersion: "unknown",
          webVersion: "unknown",
          plays: unknownPlays,
          logins: unknownLogins,
        },
      ].filter((entry) => entry.plays > 0 || entry.logins > 0),
    };
  });
  const totalPlays = dailyActivity.reduce((sum, day) => sum + day.plays, 0);
  const totalLogins = dailyActivity.reduce((sum, day) => sum + day.logins, 0);
  return {
    version: 1,
    generatedAt: new Date().toISOString(),
    today: dailyActivity.at(-1)!.date,
    filter: { surface: null, appVersion: null, webVersion: null },
    availableSurfaces: ["ios_app", "android_app", "web", "unknown"],
    availableAppVersions: ["0.1.66", "1.0", "1.0.1", "unknown"],
    availableWebVersions: ["0.1.66", "unknown"],
    totalLogins,
    loginsToday: dailyActivity.at(-1)!.logins,
    uniqueLoggedInPlayers: 286,
    registeredPlayers: 412,
    totalPlays,
    playsToday: dailyActivity.at(-1)!.plays,
    uniquePlayersWithPlays: 371,
    activePlayers: dailyActivity.at(-1)!.activePlayers,
    futurePuzzleDays: 18,
    puzzlesThrough: simpleDateId(dateDaysAgo(-18)),
    dailyActivity,
  };
}

/* ============================================================
 * Friend leaderboard — offline test fixtures
 *
 * These let the Friends and Leaderboard screens be exercised end-to-end in
 * `?test` mode without any backend/CDN calls: a canned roster (you + a few
 * friends + one outstanding invite) plus deterministic per-day scores. The
 * DataLayer's social methods swap these in when isTestMode() is true.
 * ============================================================ */

/** The fake signed-in player's id in test mode (matches SessionV1.userGuid). */
export const TEST_SELF_ID = "test-player";

interface TestFriendSeed {
  playerId: string;
  displayName: string;
}

/** The canned friends shown on the leaderboard in test mode. */
const TEST_FRIENDS: TestFriendSeed[] = [
  { playerId: "friend-ada", displayName: "Ada" },
  { playerId: "friend-grace", displayName: "Grace" },
  { playerId: "friend-linus", displayName: "Linus" },
  { playerId: "friend-margaret", displayName: "Margaret" },
];

/** ISO timestamp `days` from now (negative = in the past). */
function isoDaysFromNow(days: number): string {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

/**
 * A canned roster for test mode: you, a handful of friends, and one outstanding
 * invite you sent. Pass a display name to preserve one the user typed while
 * switching scenarios; otherwise defaults to "You".
 */
export function buildTestRoster(selfDisplayName?: string, selfPlayerId = TEST_SELF_ID): Roster {
  return {
    self: { playerId: selfPlayerId, displayName: selfDisplayName?.trim() || "You" },
    friends: TEST_FRIENDS.map((f, i) => ({ ...f, since: isoDaysFromNow(-(30 - i * 6)) })),
    outgoing: [
      {
        code: "test-pending-invite-0001",
        createdAt: isoDaysFromNow(-2),
        expiresAt: isoDaysFromNow(12),
        toLabel: "Jordan",
      },
    ],
    updatedAt: isoDaysFromNow(0),
  };
}

/** FNV-1a hash, for a stable per-(puzzle, player) seed. */
function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Deterministic 0..1 generator seeded by an integer (LCG). */
function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 0x100000000;
  };
}

/**
 * Fabricate one player's leaderboard entry for a puzzle, deterministically from
 * (puzzleId, playerId) so the board is stable across reloads. Today's entries
 * are unscored (word count + regular points only, mirroring pre-rollover
 * reality); prior days are fully scored with rarity points. You always have a
 * score; a friend occasionally sits a day out so the "Not played" row and
 * bottom-sinking behaviour are exercised too.
 */
export function buildTestLeaderboardEntry(
  puzzleId: string,
  playerId: string,
  isToday: boolean,
): LeaderboardEntry {
  const rng = makeRng(hashString(`${puzzleId}|${playerId}`));
  const isSelf = playerId === TEST_SELF_ID;
  const played = isSelf || rng() > 0.18;
  if (!played) {
    return { playerId, played: false, scored: false, wordCount: 0, regularPoints: 0 };
  }
  const wordCount = 6 + Math.floor(rng() * 16); // 6..21 words
  const regularPoints = Math.round(wordCount * (2 + rng() * 3)); // ~2..5 pts/word
  if (isToday) {
    return { playerId, played: true, scored: false, wordCount, regularPoints };
  }
  const rarityPoints = Math.floor(rng() * 5) * 25; // 0, 25, 50, 75, 100
  return {
    playerId,
    played: true,
    scored: true,
    wordCount,
    regularPoints,
    rarityPoints,
    totalPoints: regularPoints + rarityPoints,
  };
}

/** Words that are all findable on TEST_BOARD, for fabricating friend plays. */
const TEST_FOUND_WORD_POOL = [
  "TIE",
  "RED",
  "REST",
  "WIRE",
  "TIRE",
  "RIDE",
  "DIRE",
  "RITE",
  "TIER",
  "WIRED",
  "TIRED",
  "TRIED",
  "WIRDER",
];

/**
 * Fabricate one player's full play for a puzzle (board + found words) so the
 * "see a friend's words" screen works offline. Deterministic from
 * (puzzleId, playerId) and consistent with buildTestLeaderboardEntry's word
 * count. Past days assign the three medal ratings to the rarest (longest) words.
 */
export function buildTestPlayerPlay(
  puzzleId: string,
  playerId: string,
  isToday: boolean,
): PlayerPlay {
  const entry = buildTestLeaderboardEntry(puzzleId, playerId, isToday);
  if (!entry.played) {
    return { board: TEST_BOARD, played: false, scored: false, words: [], regularPoints: 0 };
  }
  const count = Math.min(entry.wordCount, TEST_FOUND_WORD_POOL.length);
  const chosen = TEST_FOUND_WORD_POOL.slice(0, count);
  const words: FoundWord[] = chosen.map((word) => ({ word, rating: null }));
  if (entry.scored) {
    // Rate the three longest found words Gold/Silver/Bronze so medals render.
    const byLengthDesc = [...words].sort((a, b) => b.word.length - a.word.length);
    const ratings: FoundWord["rating"][] = ["Gold", "Silver", "Bronze"];
    byLengthDesc.slice(0, 3).forEach((w, i) => (w.rating = ratings[i]));
  }
  return {
    board: TEST_BOARD,
    played: true,
    scored: entry.scored,
    words,
    regularPoints: entry.regularPoints,
    rarityPoints: entry.rarityPoints,
    totalPoints: entry.totalPoints,
  };
}

/**
 * Canned player stats for the Stats screen in test mode. There is no
 * `test-player.json` on the CDN, so without this the Stats screen would be
 * empty. The `letters` array is sparse and indexed by exact word length: it
 * deliberately includes both 10- and 11-letter entries (to prove they no
 * longer conflate into a "10+" bucket) and skips lengths 7\u20139 (to show
 * lengths with no record are simply omitted).
 */
export function buildTestPlayerStats(): IPlayerStats {
  const letters: IPlayerStats["letters"][number][] = [];
  letters[3] = { value: 8, placeOverall: 12, date: dateDaysAgo(5) };
  letters[4] = { value: 5, placeOverall: 2, date: dateDaysAgo(3) };
  letters[5] = { value: 3, placeOverall: 5, date: dateDaysAgo(3) };
  letters[6] = { value: 2, placeOverall: 1, date: dateDaysAgo(6) };
  letters[10] = { value: 2, placeOverall: 1, date: dateDaysAgo(7) };
  letters[11] = { value: 1, placeOverall: 1, date: dateDaysAgo(8) };

  return {
    totalPlays: undefined,
    mostFoundWords: { value: 42, placeOverall: 3, date: dateDaysAgo(5) },
    totalPoints: { value: 235, placeOverall: 4, date: dateDaysAgo(5) },
    regularPoints: { value: 120, placeOverall: 5, date: dateDaysAgo(5) },
    rarityPoints: { value: 80, placeOverall: 6, date: dateDaysAgo(5) },
    letters,
  };
}
